/// <amd-module name="Build/DesignerExtensions/YamlAssistantLibrary/Components/ContributedSnippetForm" />
import * as React from "react";
import { Components, VssComponent, WrappedComponent, IWrappedComponentProperties } from "VSS/Platform/Layout";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { ContributedSnippetFormProvider, ContributedSnippetFormData, ContributedSnippetFormType } from "Build/DesignerExtensions/YamlAssistantLibrary/Contracts/Types";

const contributedFormComponentType = "ms.vss-build-web.yaml-contributed-snippet-form-type";
const defaultFormComponentId = "ms.vss-build-web.designer-extensions.yaml-snippet-form";
const defaultFormComponentType = "yaml-snippet-form";

/**
 * A wrapper around the shared yaml-snippet-form project. 
 * If a valid snippet with inputs is provided, the default form is async loaded.
 * If a valid component with id/type is provided, that component is async loaded and and extra provided props are passed through.
 * Otherwise, check contributions of type yaml-contributed-snippet-form-type and see if they specifically
 *     target the given snippet (i.e. the contribution property 'snippetId' matches).
 * In all cases, we provide 2 callbacks in the props as suggestions on how to structure usage - one for
 *     calling when yaml is generated and another for when inputs are provided. This lets the contributed
 *     component either pass back full yaml or just the input map if the calling component wants to take care of that.
 */
export class ContributedSnippetForm extends VssComponent<ContributedSnippetFormProvider> {

    public render(): JSX.Element {
        if (this.props.snippet && this.props.snippet.inputs) {
            return <WrappedComponent { ...getDefaultComponentProps(this.props) } />
        }

        if (this.props.componentData && this.props.componentData.id && this.props.componentData.type) {
            return <WrappedComponent { ...getContributedComponentProps(this.props) } />
        }

        if (this.props.snippet && this.props.snippet.id) {
            const contributedFormData = getSnippetFormContributions(this.props.snippet.id, this.context.pageContext);

            if (contributedFormData) {
                return <WrappedComponent { ...getContributedComponentProps(this.props, contributedFormData) } />
            }
        }
        
        return <div />;
    }
}

function getDefaultComponentProps(formProps: ContributedSnippetFormProvider): IWrappedComponentProperties {
    return {
        wrappedType: defaultFormComponentType,
        dependencies: [ defaultFormComponentId ],
        ...formProps,
    };
}

function getContributedComponentProps(formProps: ContributedSnippetFormProvider, formData?: ContributedSnippetFormData): IWrappedComponentProperties {
    const componentData = formData || formProps.componentData || {} as ContributedSnippetFormData;
    const dependencies = (componentData.dependencies || []).concat(componentData.id);

    return {
        wrappedType: componentData.type,
        wrappedProps: componentData.props,
        dependencies: dependencies,
        ...formProps,
    };
}

function getSnippetFormContributions(snippetId: string, pageContext: IVssPageContext): ContributedSnippetFormData | null {
    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
    const contributions = contributionService.getContributionsByType<ContributedSnippetFormType>(contributedFormComponentType);

    for (const contributionId in contributions) {
        const contribution = contributions[contributionId];

        if (contribution.snippetId === snippetId) {
            return {
                id: contribution.componentId || contributionId,
                type: contribution.componentType || contributionId,
                dependencies: contribution.dependencies,
            };
        }
    }

    return null;
}

Components.add("yaml-contributed-snippet-form", ContributedSnippetForm);